import { createCompoundIndexValue, createLowerBoundIndexValue, createUpperBoundIndexValue, getOidRoot, assert, } from '@verdant-web/common';
import { IDBService } from '../IDBService.js';
export class OperationsStore extends IDBService {
    constructor() {
        super(...arguments);
        /**
         * Iterates over every patch for the root and every sub-object
         * of a given document. Optionally limit by timestamp.
         */
        this.iterateOverAllOperationsForDocument = async (oid, iterator, { to, from, after, mode = 'readonly', transaction: providedTx, } = {}) => {
            const transaction = providedTx || this.db.transaction('operations', mode);
            const store = transaction.objectStore('operations');
            const index = store.index('d_t');
            const startTimestamp = from || after;
            const start = startTimestamp
                ? createCompoundIndexValue(oid, startTimestamp)
                : createLowerBoundIndexValue(oid);
            const end = to
                ? createCompoundIndexValue(oid, to)
                : createUpperBoundIndexValue(oid);
            const range = IDBKeyRange.bound(start, end, !!after, false);
            const request = index.openCursor(range, 'next');
            return new Promise((resolve, reject) => {
                let previousTimestamp;
                request.onsuccess = (event) => {
                    const cursor = request.result;
                    if (cursor) {
                        const value = cursor.value;
                        assert(value.oid.startsWith(oid));
                        assert(previousTimestamp === undefined ||
                            previousTimestamp <= value.timestamp, `expected ${previousTimestamp} <= ${value.timestamp}`);
                        iterator(value, store);
                        previousTimestamp = value.timestamp;
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = (event) => {
                    reject(event);
                };
            });
        };
        this.iterateOverAllOperationsForEntity = async (oid, iterator, { after, to, mode, transaction: providedTx, }) => {
            const transaction = providedTx || this.db.transaction('operations', mode);
            const store = transaction.objectStore('operations');
            const start = after
                ? createCompoundIndexValue(oid, after)
                : createLowerBoundIndexValue(oid);
            const end = to
                ? createCompoundIndexValue(oid, to)
                : createUpperBoundIndexValue(oid);
            const range = IDBKeyRange.bound(start, end, !!after, false);
            const request = store.openCursor(range, 'next');
            return new Promise((resolve, reject) => {
                let previousTimestamp;
                request.onsuccess = (event) => {
                    const cursor = request.result;
                    if (cursor) {
                        const value = cursor.value;
                        assert(value.oid.startsWith(oid));
                        assert(previousTimestamp === undefined ||
                            previousTimestamp <= value.timestamp, `expected ${previousTimestamp} <= ${value.timestamp}`);
                        iterator(value, store);
                        previousTimestamp = value.timestamp;
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = (event) => {
                    reject(event);
                };
            });
        };
        this.iterateOverAllLocalOperations = async (iterator, { before, after, mode = 'readonly', transaction: providedTx, }) => {
            const transaction = providedTx || this.db.transaction('operations', mode);
            const store = transaction.objectStore('operations');
            const index = store.index('l_t');
            const start = after
                ? createCompoundIndexValue(true, after)
                : createLowerBoundIndexValue(true);
            const end = before
                ? createCompoundIndexValue(true, before)
                : createUpperBoundIndexValue(true);
            const range = IDBKeyRange.bound(start, end, !!after, true);
            const request = index.openCursor(range, 'next');
            return new Promise((resolve, reject) => {
                let previousTimestamp;
                request.onsuccess = (event) => {
                    const cursor = request.result;
                    if (cursor) {
                        const value = cursor.value;
                        assert(previousTimestamp === undefined ||
                            previousTimestamp <= value.timestamp, `expected ${previousTimestamp} <= ${value.timestamp}`);
                        iterator(value, store);
                        previousTimestamp = value.timestamp;
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = (event) => {
                    reject(event);
                };
            });
        };
        this.iterateOverAllOperations = async (iterator, { before, transaction, mode, }) => {
            await this.iterate('operations', (store) => {
                const end = before ? createUpperBoundIndexValue(before) : undefined;
                const range = end ? IDBKeyRange.upperBound(end, true) : undefined;
                const index = store.index('timestamp');
                return index.openCursor(range, 'next');
            }, iterator, mode, transaction);
        };
        /**
         * Adds a set of patches to the database.
         * @returns a list of affected root document OIDs.
         */
        this.addOperations = async (patches, { transaction } = {}) => {
            return this.insert(patches.map(this.addCompoundIndexes), { transaction });
        };
        this.addCompoundIndexes = (patch) => {
            return Object.assign(Object.assign({}, patch), { oid_timestamp: createCompoundIndexValue(patch.oid, patch.timestamp), l_t: createCompoundIndexValue(patch.isLocal, patch.timestamp), d_t: createCompoundIndexValue(getOidRoot(patch.oid), patch.timestamp) });
        };
        this.insert = async (operations, { transaction }) => {
            const affected = new Set();
            await this.runAll('operations', (store) => operations.map((op) => {
                affected.add(getOidRoot(op.oid));
                return store.put(op);
            }), 'readwrite', transaction);
            return Array.from(affected);
        };
        this.reset = () => {
            return this.clear('operations');
        };
    }
}
//# sourceMappingURL=OperationsStore.js.map